// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The last few seconds of where the ball went (M9-19, 11 §4).
//
// *"Trajectory trace — draw the last 3 seconds of ball path, with contact points marked."*
//
// **A point per simulation step, which is a point per millisecond.** Three seconds is three
// thousand of them, which sounds like a lot until you notice that it is two `Float64Array`s of
// three thousand and a `Uint8Array` — 48 KB, allocated once, never grown. A trail sampled per
// *frame* instead would be sixty points a second and would miss the whole of a contact: a ball
// meeting a slingshot arrives, deforms and leaves inside about four milliseconds, so a frame
// sampler draws a straight line through the one thing an author is looking at.
//
// **A ring, so the cost is bounded and the oldest point falls off.** Nothing here allocates after
// construction, because this runs inside the step loop of a machine that must hold 1000 Hz.
//
// **Contact is a property of the step, not of the point.** The contact feed reports which surface
// and how fast, but not *where* — the ball's position at that step is where, and that is the point
// this trail already holds. So a step is marked as a contact step by asking whether the feed's
// recorded count grew during it, which is one integer compare and cannot disagree with the physics.
//
// Pure of the DOM and of a clock: it is told a step index and a position, and remembers them.

import { STEP_DT, STEP_HZ } from '../../core/ts/sim/constants'

/** 11 §4's three seconds, in steps. */
export const TRAIL_SECONDS = 3
export const TRAIL_STEPS = TRAIL_SECONDS * STEP_HZ

/** One remembered position. */
export interface TrailPoint {
  readonly x: number
  readonly y: number
  /** The ball's centre height, metres (M13-25): a ribbon on the bench runs up the ramps the ball took. */
  readonly z: number
  /** The simulation step it was recorded on. */
  readonly step: number
  /** Whether the ball touched something on this step. */
  readonly contact: boolean
}

export class BallTrail {
  private readonly xs: Float64Array
  private readonly ys: Float64Array
  private readonly zs: Float64Array
  private readonly steps: Float64Array
  private readonly contacts: Uint8Array
  private written = 0

  constructor(readonly capacity: number = TRAIL_STEPS) {
    this.xs = new Float64Array(capacity)
    this.ys = new Float64Array(capacity)
    this.zs = new Float64Array(capacity)
    this.steps = new Float64Array(capacity)
    this.contacts = new Uint8Array(capacity)
  }

  /** How many points are held, up to the capacity. */
  get length(): number {
    return this.written < this.capacity ? this.written : this.capacity
  }

  /** Points recorded since it was cleared, including the ones already overwritten. */
  get recorded(): number {
    return this.written
  }

  mark(x: number, y: number, step: number, contact: boolean, z = 0): void {
    const slot = this.written % this.capacity
    this.xs[slot] = x
    this.ys[slot] = y
    this.zs[slot] = z
    this.steps[slot] = step
    this.contacts[slot] = contact ? 1 : 0
    this.written++
  }

  /**
   * The trail, oldest first.
   *
   * **Visited rather than returned as an array.** This is read once a frame by something that
   * draws it, and handing back three thousand freshly allocated objects sixty times a second is a
   * garbage-collection pause in the middle of the thing being measured.
   */
  forEach(visit: (x: number, y: number, step: number, contact: boolean, z: number) => void): void {
    const held = this.length
    const from = this.written < this.capacity ? 0 : this.written % this.capacity
    for (let i = 0; i < held; i++) {
      const slot = (from + i) % this.capacity
      visit(this.xs[slot]!, this.ys[slot]!, this.steps[slot]!, this.contacts[slot] === 1, this.zs[slot]!)
    }
  }

  /** The most recent point, or nothing when the trail is empty. */
  get last(): TrailPoint | undefined {
    if (this.written === 0) return undefined
    const slot = (this.written - 1) % this.capacity
    return {
      x: this.xs[slot]!,
      y: this.ys[slot]!,
      z: this.zs[slot]!,
      step: this.steps[slot]!,
      contact: this.contacts[slot] === 1,
    }
  }

  /**
   * Forget everything.
   *
   * **Called when the ball changes**, not only when the machine is rebuilt: a trail that ran from
   * where the last ball drained to where the next one was served would draw a line straight up the
   * playfield through the trough, which is a path no ball ever took.
   */
  clear(): void {
    this.written = 0
  }
}

/** The ribbon's speed bands (polish to M13-25), metres per second: under `RIBBON_SLOW` is slow, from `RIBBON_FAST` fast. */
export const RIBBON_SLOW = 1
export const RIBBON_FAST = 3

/** A speed's band: 0 slow, 1 between, 2 fast. */
export function speedBand(metresPerSecond: number): 0 | 1 | 2 {
  return metresPerSecond < RIBBON_SLOW ? 0 : metresPerSecond < RIBBON_FAST ? 1 : 2
}

/**
 * Every held point's speed band, oldest first, into `into` (as many as it holds); answers how many.
 *
 * The speed at a point is the distance from the one before over the steps between them, in three
 * dimensions — a ball climbing a ramp is moving. The first point takes the second's band, having
 * nothing before it. Pure arithmetic on the trail, so both clients band the same trail alike.
 */
export function ribbonBands(trail: BallTrail, into: Uint8Array): number {
  let n = 0
  let px = 0
  let py = 0
  let pz = 0
  let ps = 0
  trail.forEach((x, y, step, _contact, z) => {
    if (n >= into.length) return
    if (n > 0) {
      const dx = x - px
      const dy = y - py
      const dz = z - pz
      const steps = Math.max(1, step - ps)
      into[n] = speedBand(Math.sqrt(dx * dx + dy * dy + dz * dz) / (steps * STEP_DT))
      if (n === 1) into[0] = into[1] as number
    } else {
      into[0] = 0
    }
    px = x
    py = y
    pz = z
    ps = step
    n++
  })
  return n
}
